// Before checkout the storefront asks its own server function to apply the member's vouchers to the
// cart, so the gift card codes never reach the browser. Checkout must not depend on it: whatever
// happens, this resolves, at the latest after timeoutMs.

export const APPLY_MEMBER_VOUCHERS_TIMEOUT_MS = 8000;

export interface IMemberVouchersRequest {
  storeCode: string;
  memberEmail: string;
  cartId: string;
}

export interface IMemberVouchersResult {
  success: boolean;
  applied: number;
  lastCharacters: Array<string>;
}

interface IAttribute {
  key: string;
  value: string;
}

// The request for a cart, or null when the cart has no member email (nothing to apply) or no id.
export const memberVouchersRequest = (
  attributes: Array<IAttribute> | null | undefined,
  cartId: string | null | undefined
): IMemberVouchersRequest | null => {
  const valueOf = (key: string): string => {
    const attribute = (attributes || []).find((attr) => attr.key === key);
    return attribute && typeof attribute.value === 'string'
      ? attribute.value.trim()
      : '';
  };

  const memberEmail = valueOf('member_email');
  const storeCode = valueOf('merchstore_code');

  if (!cartId || !memberEmail || !storeCode) {
    return null;
  }

  return { storeCode, memberEmail, cartId };
};

// Only the buyer's click on Proceed to Checkout applies vouchers. The cart page also creates a cart
// when it loads, as a fallback checkout URL; applying there would create Shopify cards while the
// member is just looking at the cart.
export const checkoutVouchersRequest = (
  applyVouchers: boolean,
  attributes: Array<IAttribute> | null | undefined,
  cartId: string | null | undefined
): IMemberVouchersRequest | null =>
  applyVouchers ? memberVouchersRequest(attributes, cartId) : null;

// post sends the request (http.post in the app); its result or failure never reaches checkout.
export const applyMemberVouchersBeforeCheckout = (
  post: (request: IMemberVouchersRequest) => Promise<IMemberVouchersResult>,
  request: IMemberVouchersRequest | null,
  timeoutMs = APPLY_MEMBER_VOUCHERS_TIMEOUT_MS
): Promise<IMemberVouchersResult | null> => {
  if (!request) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      console.warn('applyMemberVouchers: timed out, continuing to checkout');
      resolve(null);
    }, timeoutMs);

    const finish = (result: IMemberVouchersResult | null) => {
      clearTimeout(timer);
      resolve(result);
    };

    let pending: Promise<IMemberVouchersResult>;
    try {
      pending = Promise.resolve(post(request));
    } catch (err) {
      pending = Promise.reject(err);
    }

    pending.then(
      (result) => finish(result || null),
      (err) => {
        console.warn(
          'applyMemberVouchers: failed, continuing to checkout',
          err && err.message ? err.message : ''
        );
        finish(null);
      }
    );
  });
};
